import { Box, Grid, Typography, useMediaQuery, useTheme } from '@mui/material'
import React, { useCallback, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { useLoggedInUser } from '../../../app/hooks/useLoggedInUser'
import { useGetCustomersQuery } from '../../../app/redux-fetch/apiQuery'
import { convertToLabelValue } from '../../../app/utils'
import CustomAlert from '../../../components/Alerts/CustomAlert'
import BoxWhiteContainer from '../../../components/Containers/BoxWhiteContainer'
import DefaultSpacingGrid from '../../../components/Containers/DefaultSpacingGrid'
import FormInput from '../../../components/Forms/FormInput'
import FormSelect from '../../../components/Forms/FormSelect'
import { PageHeader } from '../../../components/Headers/styles'
import commonMessages from '../../../components/Messages/commonMessages'
import InfoDisplayItem from '../../../components/Typographies/InfoDisplayItem'
import { LabelItem } from '../../../components/Typographies/LabelItem'
import { useOrderFormContext } from '../context/OrderFormContext'
import { useOrderStatus } from '../hooks/useOrderStatus'
import messages from './messages'

const ShipmentInformation: React.FC = () => {
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const { watch, setValue } = useFormContext()

  const { currentPage } = useOrderFormContext()
  const { formatMessage } = useIntl()

  const customer = watch('customerId')
  const customerReferenceNumber = watch('customerReferenceNumber')

  const description = watch('description')
  const shipmentNumber = watch('shipmentNumber')
  const status = watch('status')

  const { status: orderStatus, isEdit } = useOrderStatus()

  const { isTenantUser, isCustomerUser, tenantId: loggedInTenantId } = useLoggedInUser()

  const tenantId = watch('tenantId') || loggedInTenantId

  const { data: customers = [], isSuccess: customerLoaded } = useGetCustomersQuery(tenantId, {
    skip: isCustomerUser,
  })

  const getCustomerName = (customerId: number) => {
    return customers.find((customer) => customer.id === customerId)?.name
  }
  const enabledCustomers = useMemo(() => {
    return convertToLabelValue(customers.filter((customer) => customer.disabled === false))
  }, [customers])

  const handleCustomerChanged = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setValue('bookings', [
        {
          shipFrom: {
            transitDetails: {
              carrierOption: 'noPreference',
            },
          },
        } as any,
      ])
    },
    [setValue]
  )

  const getStatusDisplayValue = useCallback(
    (status: string) => {
      switch (status) {
        case 'Draft':
          return formatMessage(messages.draftStatus)
        case 'Created':
          return formatMessage(messages.createdStatus)
        case 'Submitted':
          return formatMessage(messages.submittedStatus)
        case 'Accepted':
          return formatMessage(messages.acceptedStatus)
        case 'Under Review':
          return formatMessage(messages.underReview)
        case 'Completed':
          return formatMessage(messages.completed)
        case 'Archived':
          return formatMessage(messages.archived)
        case 'Rejected':
          return formatMessage(messages.buttonReject)
        case 'Abandoned':
          return formatMessage(messages.abandoned)
        default:
          return formatMessage(messages.shipmentNew)
      }
    },
    [formatMessage]
  )

  if (currentPage !== 1) {
    return (
      <BoxWhiteContainer>
        <Grid container>
          <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
            <LabelItem>{formatMessage(messages.shipmentInformation)}</LabelItem>
          </Grid>
          {isTenantUser ? (
            <>
              <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                <InfoDisplayItem
                  label={formatMessage(messages.customer)}
                  value={getCustomerName(customer) || 'N/A'}
                  isSmallScreen={isSmallScreen}
                />
              </Grid>
              <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                <InfoDisplayItem
                  label={formatMessage(messages.customerReferenceNumber)}
                  value={customerReferenceNumber || 'N/A'}
                  isSmallScreen={isSmallScreen}
                />
              </Grid>
              <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                <InfoDisplayItem
                  label={formatMessage(messages.shipmentNumber)}
                  value={shipmentNumber || 'N/A'}
                  isSmallScreen={isSmallScreen}
                />
              </Grid>
              <Grid
                item
                xs={12}
                md={3}
                sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}
              >
                <Box
                  display='flex'
                  alignItems='baseline'
                  minWidth={isSmallScreen ? '100%' : '200px'}
                  sx={{
                    width: '100%',
                    overflow: 'hidden',
                    position: 'relative',
                    gap: '4px',
                    mb: '4px'
                  }}
                >
                  <Typography
                    variant='subtitle1'
                    sx={{
                      fontWeight: 400,
                      paddingLeft: '28px',
                      color: '#636363',
                      textAlign: 'left',
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {`${formatMessage(messages.status)}:`}
                  </Typography>
                  <Typography
                    component='span'
                    variant='subtitle1'
                    color='primary'
                    sx={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      flex: 1,
                      minWidth: 0,
                      fontWeight: 700,
                      fontSize: '1.5rem',
                    }}
                  >
                    {getStatusDisplayValue(status)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={9} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                <InfoDisplayItem
                  label={formatMessage(messages.description)}
                  value={description || 'N/A'}
                  isSmallScreen={isSmallScreen}
                />
              </Grid>
            </>
          ) : (
            <>
              <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                <InfoDisplayItem
                  label={formatMessage(messages.description)}
                  value={description || 'N/A'}
                  isSmallScreen={isSmallScreen}
                />
              </Grid>
              <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                <InfoDisplayItem
                  label={formatMessage(messages.customerReferenceNumber)}
                  value={customerReferenceNumber || 'N/A'}
                  isSmallScreen={isSmallScreen}
                />
              </Grid>
              <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                <InfoDisplayItem
                  label={formatMessage(messages.shipmentNumber)}
                  value={shipmentNumber || 'N/A'}
                  isSmallScreen={isSmallScreen}
                />
              </Grid>
              <Grid
                item
                xs={12}
                md={3}
                sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}
              >
                <Box
                  display='flex'
                  alignItems='baseline'
                  minWidth={isSmallScreen ? '100%' : '200px'}
                  sx={{
                    width: '100%',
                    overflow: 'hidden',
                    position: 'relative',
                    gap: '4px',
                  }}
                >
                  <Typography
                    variant='subtitle1'
                    sx={{
                      fontWeight: 400,
                      paddingLeft: '28px',
                      color: '#636363',
                      textAlign: 'left',
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {`${formatMessage(messages.status)}:`}
                  </Typography>
                  <Typography
                    component='span'
                    variant='subtitle1'
                    color='primary'
                    sx={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      flex: 1,
                      minWidth: 0,
                      fontWeight: 700,
                      fontSize: '1.5rem',
                    }}
                  >
                    {getStatusDisplayValue(status)}
                  </Typography>
                </Box>
              </Grid>
            </>
          )}
        </Grid>
      </BoxWhiteContainer>
    )
  } else {
    return (
      <>
        {enabledCustomers.length === 0 && customerLoaded && (
          <CustomAlert
            severity='error'
            title='Error'
            message={formatMessage(messages.noCustomersAvailable)}
          />
        )}
        <DefaultSpacingGrid container>
          <Grid
            item
            xs={12}
            md={12}
            sx={{ display: 'flex', justifyContent: 'flex-start', mt: '8px' }}
          >
            <PageHeader>{formatMessage(messages.shipmentInformation)}</PageHeader>
          </Grid>
          <Grid
            item
            xs={12}
            md={2}
            sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}
          >
            <Box
              display='flex'
              alignItems='baseline'
              minWidth={isSmallScreen ? '100%' : '200px'}
              sx={{
                width: '100%',
                overflow: 'hidden',
                position: 'relative',
                gap: '4px',
              }}
            >
              <Typography
                variant='subtitle1'
                sx={{
                  fontWeight: 400,
                  color: '#636363',
                  textAlign: 'left',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  paddingLeft: '28px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {`${formatMessage(messages.status)}:`}
              </Typography>
              <Typography
                component='span'
                variant='subtitle1'
                color='primary'
                sx={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  flex: 1,
                  minWidth: 0,
                  fontWeight: 700,
                  fontSize: '1.5rem',
                }}
              >
                {getStatusDisplayValue(status)}
              </Typography>
            </Box>
          </Grid>
          {isTenantUser && (
            <Grid item xs={12} md={2}>
              <FormSelect
                name='customerId'
                label={formatMessage(messages.customer)}
                options={enabledCustomers}
                disabled={isCustomerUser || status}
                required={true}
                onChange={handleCustomerChanged}
                fullWidth
              />
            </Grid>
          )}
          <Grid item xs={12} md={isTenantUser ? 4 : 5}>
            <FormInput
              name='description'
              label={formatMessage(messages.description)}
              required={true}
              placeholder={formatMessage(commonMessages.pleaseEnter)}
              fullWidth
              maxLength={200}
            />
          </Grid>
          <Grid item xs={12} md={isTenantUser ? 2 : 3}>
            <FormInput
              name='customerReferenceNumber'
              label={formatMessage(messages.customerReferenceNumber)}
              placeholder={formatMessage(commonMessages.pleaseEnter)}
              fullWidth
              maxLength={50}
              inputPattern={/[^a-zA-Z0-9]/g}
            />
          </Grid>
          <Grid item xs={12} md={isTenantUser ? 2 : 2}>
            <FormInput
              name='shipmentNumber'
              label={formatMessage(messages.shipmentNumber)}
              placeholder={formatMessage(messages.autoGenerated)}
              fullWidth
              helperText={!isEdit ? formatMessage(messages.autoGenerated) : ''}
              disabled={true}
            />
          </Grid>
        </DefaultSpacingGrid>
      </>
    )
  }
}

export default ShipmentInformation
